import {
  AvailableEntities,
  RbacHandlerFactory,
  ReturnFieldEnum,
  UsersAndGroupsPicker as UsersAndGroupsPickerBase,
} from '@agilelab/plugin-wb-platform';
import { RbacSubjectHandlerProvider } from '@agilelab/plugin-wb-rbac';
import FormControl from '@material-ui/core/FormControl';
import { useMemo } from 'react';
import { extractCustomProperties, isHidden } from '../../utils';
import { UsersAndGroupsPickerProps } from './schema';

const DEFAULT_PAGE_SIZE = 20;

const rbacHandlerFactory: RbacHandlerFactory = (
  nunjucksApi,
  catalogApi,
  availableKinds,
  options,
) => ({
  template: {
    type: 'user',
    returnField: ReturnFieldEnum.ref,
    displayField: '{{spec.profile.displayName}}',
    columns: [{ name: 'Subject', value: 'name' }],
  },
  providers: new Map([
    [
      'user',
      new RbacSubjectHandlerProvider(nunjucksApi, catalogApi, availableKinds, {
        roleIds: options.roleIds,
        scopes: options.scopes,
        resolveGroups: options.resolveGroups,
        pageSize: options.pageSize,
      }),
    ],
  ]),
});

export const UsersAndGroupsPicker = (props: UsersAndGroupsPickerProps) => {
  const {
    onChange,
    schema: {
      title = 'Users and Groups',
      description = 'Select users and groups',
    },
    required,
    uiSchema,
    rawErrors,
    idSchema,
    formData,
    formContext,
  } = props;

  const customProps = extractCustomProperties(uiSchema);
  const disabled = uiSchema['ui:disabled'] ?? false;
  const defaultValue = props.schema?.default as string | string[] | undefined;

  const uiOptions = useMemo(() => uiSchema['ui:options'] ?? {}, [uiSchema]);
  const pageSize =
    (uiOptions.pageSize as number | undefined) ?? DEFAULT_PAGE_SIZE;
  const allowMultiple = (uiOptions.allowMultiple as boolean) ?? false;

  const allowedKinds = useMemo(() => {
    const kinds = uiOptions.allowedKinds as string[] | undefined;
    if (!kinds) return [AvailableEntities.USER, AvailableEntities.GROUP];
    return kinds.map(k =>
      k.toLowerCase() === 'group'
        ? AvailableEntities.GROUP
        : AvailableEntities.USER,
    );
  }, [uiOptions.allowedKinds]);

  const rbacFilters = useMemo(() => {
    const roleIds = uiOptions.roleIds as string[] | undefined;
    const scopes = uiOptions.scopes as string[] | undefined;
    const resolveGroups = uiOptions.resolveGroups as boolean | undefined;
    if (!roleIds?.length && !scopes?.length) return undefined;
    return { roleIds, scopes, resolveGroups, pageSize };
  }, [uiOptions.roleIds, uiOptions.scopes, uiOptions.resolveGroups, pageSize]);

  const currentValue = useMemo(() => {
    if (formData === '' || formData === undefined || formData === null)
      return undefined;
    return formData;
  }, [formData]);

  const defaultValues = useMemo(() => {
    if (!defaultValue) return undefined;
    return Array.isArray(defaultValue) ? defaultValue : [defaultValue];
  }, [defaultValue]);

  return (
    <FormControl
      style={{ display: isHidden(uiSchema) ? 'none' : undefined }}
      required={required}
      error={rawErrors?.length > 0}
    >
      <UsersAndGroupsPickerBase
        title={title}
        description={description}
        required={required ?? false}
        id={idSchema?.$id}
        value={currentValue}
        defaultValue={defaultValues}
        disabled={disabled}
        onSelect={onChange}
        freeSolo={false}
        multiSelection={allowMultiple}
        allowedKinds={allowedKinds}
        rbacFilters={rbacFilters}
        rbacHandlerFactory={rbacHandlerFactory}
        formContext={formContext}
        {...customProps}
      />
    </FormControl>
  );
};
